import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntake } from "../lib/intake-context-value";
import { API_BASE } from "../lib/api";
import { useTurnstile } from "../lib/turnstile";
import { fireEvent } from "../lib/tracking";

export default function StartPage() {
  const navigate = useNavigate();
  const { setToken } = useIntake();
  const turnstile = useTurnstile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!turnstile.token && !loading;

  const handleStart = async () => {
    if (!turnstile.token) {
      setError("Please complete the security check first.");
      return;
    }
    setLoading(true);
    setError(null);
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/intake/vesper5/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "cf-turnstile-token": turnstile.token,
        },
      });
    } catch (e) {
      console.error("[start] fetch failed", e);
      setError("Network error. Please try again.");
      setLoading(false);
      return;
    }
    let json: { success: boolean; data?: { token: string }; error?: string };
    try {
      json = await res.json();
    } catch (e) {
      console.error("[start] response parse failed", { status: res.status, error: e });
      setError("The server returned an unexpected response. Please refresh and try again.");
      setLoading(false);
      return;
    }
    if (!json.success) {
      // Reset the widget so the user can re-verify on any Turnstile-shaped
      // failure (403 + the message hints) AND on rate-limit (429) since
      // the existing token will have expired by the time the window
      // re-opens. Don't reset on the 5xx fallthrough — server is sad,
      // a fresh token wouldn't help.
      const isTurnstileFailure =
        res.status === 403 ||
        res.status === 429 ||
        /turnstile|security|verification/i.test(json.error ?? "");
      if (isTurnstileFailure) turnstile.reset();
      setError(json.error || "Failed to start session");
      setLoading(false);
      return;
    }
    const intakeToken = json.data!.token;
    setToken(intakeToken);
    fireEvent("prescreen_start", "vesper5-us", undefined, intakeToken);
    navigate("/prescreen");
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-[24px] font-extralight text-gray-900 text-balance" style={{ letterSpacing: "-0.4px" }}>
          Before we begin
        </h1>
        <p className="text-[14px] text-gray-600 text-pretty">
          Please confirm you are not a robot to continue.
        </p>
      </div>

      <div
        className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center gap-4"
        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)", minHeight: 180 }}
        aria-label="Security verification"
      >
        {/* Turnstile mounts here */}
        <div id={turnstile.containerId} aria-busy={turnstile.loading} />
        {turnstile.loading && (
          <span className="text-[13px] text-gray-500">Loading security check…</span>
        )}
        {turnstile.error && (
          <p className="text-[12px] text-[var(--color-status-danger-text)] text-center max-w-[300px]" role="status">
            {turnstile.error}
          </p>
        )}
        <p className="text-[12px] text-gray-500 text-center">
          Protected by Cloudflare Turnstile
        </p>
      </div>

      {error && (
        <div className="bg-[var(--color-status-danger-bg)] rounded-xl p-3 text-[13px] text-[var(--color-status-danger-text)]" role="alert">
          {error}
        </div>
      )}

      <button
        onClick={handleStart}
        disabled={!canSubmit}
        className="w-full py-3 rounded-xl bg-gray-900 text-white text-[14px] font-medium hover:bg-gray-800 active:scale-[0.97] transition-[transform,opacity,background-color] duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? "Starting..." : "Continue"}
      </button>

      <p className="text-center text-[12px] text-gray-500">
        By continuing, you agree to our privacy practices.
      </p>
    </div>
  );
}
